import { useEffect, useRef, useState } from 'react'
import { f2 } from '../../copyFlow2'
import { v3 } from '../../copyV3'
import { rc } from '../../copyR3Cities'
import { buildCity, cityStatus } from '../../lib/city'
import { Button } from '../../ui/Button'
import { Spinner } from '../../ui/Spinner'

const EVERY_MS = 4000
const GIVE_UP_MS = 3 * 60 * 1000

/**
 * Another city (WP16 item 7): a place we have not set up yet starts the existing setting-up flow ("Getting {city}
 * ready"), polls `city {status}` and, once live, asks Browse to load again. Cities are built on demand now.
 */
export function CitySetupState({ slug, label, status, onReady, found = 0 }: { slug: string; label: string; status: string; onReady: () => void; found?: number }) {
  const [failed, setFailed] = useState(false)
  const [round, setRound] = useState(0)
  // Alvin 10/09 (search any place): a progress feel while it builds. Time-based (the server sends no percentage):
  // eases toward 95% over about a minute, never claims done until the list is live
  const [since] = useState(() => Date.now())
  const [, tick] = useState(0)
  useEffect(() => { const t = window.setInterval(() => tick((n) => n + 1), 1000); return () => window.clearInterval(t) }, [])
  const pct = Math.min(95, Math.round(95 * (1 - Math.exp(-(Date.now() - since) / 30000))))
  const ready = useRef(onReady)
  ready.current = onReady
  useEffect(() => {
    if (!slug) return
    let live = true
    let timer = 0
    const started = Date.now()
    const tick = async () => {
      if (!live) return
      if (Date.now() - started > GIVE_UP_MS) return setFailed(true)
      try {
        const s = await cityStatus(slug)
        if (!live) return
        if (s.status === 'live') return ready.current()
        if (s.status === 'failed' || s.status === 'limit') return setFailed(true)
      } catch { /* keep polling */ }
      timer = window.setTimeout(tick, EVERY_MS)
    }
    ;(async () => {
      try {
        if (status === 'new' || round > 0) await buildCity(slug)
      } catch {
        if (live) setFailed(true)
        return
      }
      timer = window.setTimeout(tick, EVERY_MS)
    })()
    return () => {
      live = false
      window.clearTimeout(timer)
    }
  }, [slug, status, round])

  return (
    <div role="status" aria-live="polite" data-review-id="place-building" className="mt-10 max-w-[34rem] pb-8">
      {failed ? (
        <>
          <p className="t-headline text-ink">{f2('city.failed')}</p>
          {/* Round 3 Lane 6 (R-49 hook): the wish is kept (city_interest), so say so */}
          <p data-review-id="city-wish-saved" className="t-body mt-2 text-ink-2">{rc('rc.not_ready_body')}</p>
          <Button className="mt-6" onClick={() => { setFailed(false); setRound((r) => r + 1) }}>{f2('common.retry')}</Button>
        </>
      ) : (
        <>
          <Spinner size={28} label={false} className="text-ink-2" />
          <h2 className="t-title mt-5">{v3('v3.search_building_title', { city: label })}</h2>
          <p className="t-body mt-3 text-ink-2">{v3('v3.search_building_body')}</p>
          <div className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-sunk" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={v3('v3.search_building_title', { city: label })}>
            <div className="h-full rounded-full bg-accent transition-[width] duration-1000 ease-linear" style={{ width: `${pct}%` }} />
          </div>
          {found > 0 && <p className="t-meta mt-2 text-ink-2">{v3('v3.search_building_found', { count: found })}</p>}
          <p data-review-id="city-wish-saved" className="t-meta mt-3 text-ink-2">{rc('rc.wish_saved', { city: label })}</p>
        </>
      )}
    </div>
  )
}
